/**
 * The two note lists on a ticket: Resolution and Activity Log.
 *
 * One component, because they were one component in legacy too — two files of
 * five hundred lines each, differing in the word on the tab, the store they
 * called and the endpoint that store called. Both are `helpdesk_notes` with a
 * `kind` here, so this takes the kind and the words to use and is otherwise
 * the same screen twice, which is what it always was.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ClipboardList, Pencil, Plus, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";

import { AttachmentList, AttachmentPicker } from "@/components/helpdesk/attachments";
import { Button, IconButton } from "@/components/ui/button";
import { ConfirmDialog, Modal } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Field, Form, Input, Textarea } from "@/components/ui/field";
import { SkeletonLines } from "@/components/ui/skeleton";
import { Tooltip } from "@/components/ui/tooltip";
import { api, errorMessage, ifMatch } from "@/services/api";
import type { TranslationKey } from "@/constants/i18n";
import {
  type AttachmentDraft,
  HELPDESK_KEY,
  TICKETS_ENDPOINT,
  isClosed,
  type Note,
  type NoteKind,
  notesQuery,
  type Ticket,
  toAttachmentInput,
  uploadDrafts,
} from "@/services/helpdesk";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";

const FORM_ID = "helpdesk-note-form";

/** What each list calls itself, and what it calls one of its entries. */
const WORDS: Record<
  NoteKind,
  { title: TranslationKey; add: TranslationKey; empty: TranslationKey }
> = {
  resolution: {
    title: "hd.resolutionNotes",
    add: "hd.addResolution",
    empty: "hd.noResolutionBody",
  },
  activity_log: {
    title: "hd.activityLogs",
    add: "hd.addLog",
    empty: "hd.noLogsBody",
  },
};

export function TicketNotes({ ticket, kind }: { ticket: Ticket; kind: NoteKind }) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const allow = useCan();
  const notes = useQuery(notesQuery(ticket.id, kind));
  const [editing, setEditing] = useState<Note | "new" | null>(null);
  const [removing, setRemoving] = useState<Note | null>(null);

  const words = WORDS[kind];
  const closed = isClosed(ticket.status);
  const canAdd = allow("add__helpdesk_note") && !closed;
  const canChange = allow("change__helpdesk_note") && !closed;
  const canDelete = allow("delete__helpdesk_note");
  const rows = notes.data ?? [];

  const remove = useMutation({
    mutationFn: async (note: Note) =>
      api.delete(`/helpdesk/tickets/${ticket.id}/notes/${note.id}`, ifMatch(note)),
    onSuccess: async () => {
      toast.success(t("hd.noteDeleted"));
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: HELPDESK_KEY });
      await queryClient.invalidateQueries({ queryKey: [TICKETS_ENDPOINT] });
    },
    onError: (failure) => toast.error(errorMessage(failure, t("hd.deleteFailed"))),
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-title-3 text-heading">{t(words.title)}</h3>
        {/* Hidden when the right is missing, disabled with a reason when the
            ticket is closed: one of those is "not for you", the other is "not
            now", and they should not look the same. */}
        {allow("add__helpdesk_note") ? (
          closed ? (
            <Tooltip label={t("hd.closedNotes")}>
              <span>
                <Button disabled>
                  <Plus className="size-4" aria-hidden />
                  {t(words.add)}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button onClick={() => setEditing("new")}>
              <Plus className="size-4" aria-hidden />
              {t(words.add)}
            </Button>
          )
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {notes.isLoading ? (
          <SkeletonLines rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title={t(words.title)}
            description={t(words.empty)}
            {...(canAdd
              ? {
                  action: (
                    <Button onClick={() => setEditing("new")}>
                      <Plus className="size-4" aria-hidden />
                      {t(words.add)}
                    </Button>
                  ),
                }
              : {})}
          />
        ) : (
          <ol className="flex flex-col gap-3">
            {rows.map((note, index) => (
              <li key={note.id} className="rounded-card bg-surface-raised p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-eyebrow text-fg-subtle">
                      {/* Numbered from the top, which is newest: the entries
                          are read as a stack, and "01" is the latest thing
                          that happened, as legacy numbered them. */}
                      {String(rows.length - index).padStart(2, "0")}
                    </p>
                    <h4 className="text-title-3 text-heading truncate">{note.title}</h4>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {canChange ? (
                      <IconButton
                        label={t("common.edit")}
                        size="icon-sm"
                        onClick={() => setEditing(note)}
                      >
                        <Pencil aria-hidden />
                      </IconButton>
                    ) : null}
                    {canDelete ? (
                      <IconButton
                        label={t("common.delete")}
                        size="icon-sm"
                        onClick={() => setRemoving(note)}
                      >
                        <Trash2 aria-hidden />
                      </IconButton>
                    ) : null}
                  </div>
                </div>
                <p className="text-fg text-body mt-2 whitespace-pre-wrap">{note.description}</p>
                <AttachmentList files={note.attachments} className="mt-3" />
                <p className="text-fg-subtle text-footnote mt-3">
                  {(note.created_by_name ?? "—") +
                    " · " +
                    format(new Date(note.created_at), "MMM d, yyyy h:mm a")}
                </p>
              </li>
            ))}
          </ol>
        )}
      </div>

      {editing ? (
        <NoteForm
          ticketId={ticket.id}
          kind={kind}
          note={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          open
          onClose={() => setRemoving(null)}
          onConfirm={() => remove.mutate(removing)}
          tone="danger"
          title={t("hd.deleteNote")}
          message={t("hd.deleteNoteBody").replace("{title}", removing.title)}
          confirmLabel={t("common.delete")}
          loading={remove.isPending}
        />
      ) : null}
    </div>
  );
}

function NoteForm({
  ticketId,
  kind,
  note,
  onClose,
}: {
  ticketId: number;
  kind: NoteKind;
  note: Note | null;
  onClose: () => void;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(note?.title ?? "");
  const [description, setDescription] = useState(note?.description ?? "");
  const [files, setFiles] = useState<AttachmentDraft[]>(note?.attachments ?? []);
  const [touched, setTouched] = useState(false);

  const missingTitle = title.trim().length === 0;
  const missingDescription = description.trim().length === 0;

  const save = useMutation({
    mutationFn: async () => {
      // Stored on save, not on pick: see `uploadDrafts`.
      const attachments = toAttachmentInput(await uploadDrafts(files));
      const body = {
        title: title.trim(),
        description: description.trim(),
        attachments,
      };
      if (note === null) {
        return api.post(`/helpdesk/tickets/${ticketId}/notes`, { ...body, kind });
      }
      return api.patch(`/helpdesk/tickets/${ticketId}/notes/${note.id}`, body, ifMatch(note));
    },
    onSuccess: async () => {
      toast.success(note === null ? t("hd.noteAdded") : t("hd.noteSaved"));
      await queryClient.invalidateQueries({ queryKey: HELPDESK_KEY });
      await queryClient.invalidateQueries({ queryKey: [TICKETS_ENDPOINT] });
      onClose();
    },
    onError: (failure) => toast.error(errorMessage(failure, t("hd.saveFailed"))),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (missingTitle || missingDescription) return;
    save.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={note === null ? t(WORDS[kind].add) : t("hd.editNote")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={save.isPending}
            loadingLabel={t("new.saving")}
          >
            {t("common.submit")}
          </Button>
        </>
      }
    >
      <Form id={FORM_ID} noValidate onSubmit={submit} className="grid gap-4 pb-2">
        <Field
          label={t("hd.noteTitle")}
          required
          htmlFor="note-title"
          {...(touched && missingTitle ? { error: t("hd.titleRequired") } : {})}
        >
          <Input
            id="note-title"
            value={title}
            maxLength={255}
            aria-invalid={touched && missingTitle ? true : undefined}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t("hd.noteTitlePlaceholder")}
          />
        </Field>
        <Field
          label={t("hd.noteDescription")}
          required
          htmlFor="note-description"
          {...(touched && missingDescription ? { error: t("hd.descriptionRequired") } : {})}
        >
          <Textarea
            id="note-description"
            rows={5}
            value={description}
            aria-invalid={touched && missingDescription ? true : undefined}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t("hd.noteDescriptionPlaceholder")}
          />
        </Field>
        <Field label={t("hd.attachments")} htmlFor="note-attachments">
          <AttachmentPicker id="note-attachments" value={files} onChange={setFiles} />
        </Field>
      </Form>
    </Modal>
  );
}
